import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import OfferCard from "../casino/OfferCard";
import casinoService from "../../../services/casinoService";
import logger from "../../utils/logger";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

/**
 * Special Offers Modal — promotional dialog shown post-signup / post-bonus.
 * Now uses Radix Dialog (focus trap, scroll-lock, ESC, aria-modal for free)
 * instead of the hand-rolled overlay.
 */
const SpecialOffersModal = ({
  isOpen,
  onClose,
  filterType = "first_time",
  title = "🎉 Welcome Bonus!",
  description = "Limited Time: Huge bonuses for new players",
}) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timerData, setTimerData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;
    const fetchOffers = async () => {
      try {
        setLoading(true);
        const [offersResponse, timerDataResponse] = await Promise.all([
          casinoService.getOffers({}),
          casinoService.getTimerData().catch((error) => {
            logger.warn(
              "SpecialOffersModal: Failed to fetch timer data",
              error,
            );
            return null;
          }),
        ]);

        setTimerData(timerDataResponse);

        let allOffers = [];
        if (offersResponse?.data && Array.isArray(offersResponse.data)) {
          allOffers = offersResponse.data;
        }

        let targetOffer = null;
        if (filterType === "first_time") {
          targetOffer = allOffers.find((o) =>
            o.name?.toLowerCase().includes("1st purchase"),
          );
        } else if (filterType === "second_time") {
          targetOffer = allOffers.find((o) =>
            o.name?.toLowerCase().includes("2nd purchase"),
          );
        }

        setOffers(targetOffer ? [targetOffer] : []);
      } catch (error) {
        logger.error("Error fetching offers:", error);
        setOffers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
  }, [isOpen, filterType]);

  const handleClaimBonus = () => {
    if (offers.length > 0) {
      onClose();
      navigate(`/checkout/${offers[0].id}`);
    }
  };

  const cleanTitle = title.replace("🎉 ", "");

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto space-y-4">
        <DialogHeader>
          <DialogTitle className="text-2xl">{cleanTitle}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div
            className="relative rounded-xl shadow-lg overflow-hidden animate-pulse bg-[url('/assets/bg/bg-4-1600.jpg')] bg-cover bg-center"
          >
            <div className="relative z-10 flex flex-col h-full">
              <div className="flex-1 p-3 md:p-4 space-y-3">
                <div className="h-4 bg-muted/30 rounded" />
                <div className="h-20 bg-muted/30 rounded" />
                <div className="h-8 bg-muted/30 rounded" />
              </div>
              <div className="bg-black/90 px-3 py-2 md:px-4 md:py-3">
                <div className="h-6 bg-muted/30 rounded" />
              </div>
            </div>
          </div>
        ) : offers.length > 0 ? (
          <>
            {offers.map((offer) => (
              <OfferCard
                key={offer.id}
                offer={offer}
                onBuyNow={() => {
                  onClose();
                  navigate(`/checkout/${offer.id}`);
                }}
                showTimer={
                  filterType === "first_time" || filterType === "second_time"
                }
                timerData={timerData}
                className="transform transition-transform duration-200 hover:scale-105 focus-within:scale-[1.02]"
              />
            ))}

            <div>
              <Button onClick={handleClaimBonus} size="lg" className="w-full">
                Claim Bonus
              </Button>
              <p className="text-muted-foreground text-sm mt-2 text-center">
                Don't miss out on these limited-time bonuses!
              </p>
            </div>
          </>
        ) : (
          <div className="text-center py-8 space-y-4">
            <h3 className="text-xl font-semibold">
              No special offers available
            </h3>
            <p className="text-muted-foreground">
              Check back later for amazing deals!
            </p>
            <Button
              onClick={() => {
                onClose();
                navigate("/offers");
              }}
            >
              Browse store
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SpecialOffersModal;
